import React from "react";

/**
 * Full wangram.com wordmark badge (680x260 aspect).
 * `className` controls sizing (usually width) via CSS.
 */
export function WangramWordmark({ className = "h-10", ariaLabel = "wangram.com logo" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 800 260"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <defs>
        <linearGradient id="wg-iris" x1="0" y1="0" x2="0.2" y2="1">
          <stop offset="0" stopColor="#8CFFB4" />
          <stop offset="40%" stopColor="#1FD174" />
          <stop offset="100%" stopColor="#04432A" />
        </linearGradient>
        <linearGradient id="wg-lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2B322E" />
          <stop offset="100%" stopColor="#050706" />
        </linearGradient>
        <linearGradient id="wg-pupil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0B120E" />
          <stop offset="100%" stopColor="#000000" />
        </linearGradient>
        <linearGradient id="wg-word" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F2FFF7" />
          <stop offset="100%" stopColor="#BFE9CF" />
        </linearGradient>
        <radialGradient id="wg-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#25E07F" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#25E07F" stopOpacity="0" />
        </radialGradient>
        <clipPath id="wg-eyeClip">
          <path d="M28 130 Q130 34 232 130 Q130 226 28 130 Z" />
        </clipPath>
      </defs>

      <rect x="0" y="0" width="800" height="260" rx="34" fill="#0A0D0B" />
      <rect x="1.5" y="1.5" width="797" height="257" rx="32.5" fill="none" stroke="#17301F" strokeWidth="3" />
      <circle cx="130" cy="130" r="115" fill="url(#wg-glow)" />

      <g clipPath="url(#wg-eyeClip)">
        <rect x="20" y="20" width="230" height="220" fill="url(#wg-lid)" />
        <circle cx="130" cy="130" r="47" fill="url(#wg-iris)" />
        <circle cx="130" cy="130" r="47" fill="none" stroke="#0A2E1C" strokeWidth="3" />
        <g stroke="#0B3A22" strokeOpacity="0.45" strokeWidth="2">
          <path d="M130 88 L130 172 M88 130 L172 130 M100 100 L160 160 M160 100 L100 160" />
        </g>
        <path d="M130 103 L153 144 L107 144 Z" fill="url(#wg-pupil)" />
        <path d="M130 103 L153 144 L107 144 Z" fill="none" stroke="#6BFFA8" strokeWidth="2.5" strokeLinejoin="round" strokeOpacity="0.85" />
        <ellipse cx="108" cy="108" rx="13" ry="9" fill="#EAFFF2" opacity="0.5" transform="rotate(-25 108 108)" />
      </g>

      <path d="M28 130 Q130 34 232 130" fill="none" stroke="#3CE98C" strokeWidth="6" strokeLinecap="round" />
      <path d="M28 130 Q130 226 232 130" fill="none" stroke="#17A45D" strokeWidth="6" strokeLinecap="round" />

      <g stroke="#1B6B3F" strokeWidth="5" strokeLinecap="round">
        <path d="M60 74 L50 58" />
        <path d="M130 48 L130 30" />
        <path d="M200 74 L210 58" />
      </g>

      <g fontFamily="Helvetica Neue, Helvetica, Arial, sans-serif">
        <text x="272" y="146" fontSize="72" fontWeight="700" letterSpacing="-1" fill="url(#wg-word)">
          wangram<tspan fill="#2ADB80">.com</tspan>
        </text>
        <text x="276" y="180" fontSize="19" fontWeight="500" letterSpacing="7.5" fill="#5C8A70">
          SEE&#160;FURTHER
        </text>
      </g>
    </svg>
  );
}

/**
 * Icon-only variant (the stylised eye) — no wordmark. 1:1 aspect.
 * Use this next to the wordmark text in tight headers.
 */
export function WangramIcon({ className = "w-8 h-8", ariaLabel = "wangram" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 260 260"
      role="img"
      aria-label={ariaLabel}
      className={className}
    >
      <defs>
        <linearGradient id="wgi-iris" x1="0" y1="0" x2="0.2" y2="1">
          <stop offset="0" stopColor="#8CFFB4" />
          <stop offset="40%" stopColor="#1FD174" />
          <stop offset="100%" stopColor="#04432A" />
        </linearGradient>
        <linearGradient id="wgi-lid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2B322E" />
          <stop offset="100%" stopColor="#050706" />
        </linearGradient>
        <linearGradient id="wgi-pupil" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0B120E" />
          <stop offset="100%" stopColor="#000000" />
        </linearGradient>
        <radialGradient id="wgi-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#25E07F" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#25E07F" stopOpacity="0" />
        </radialGradient>
        <clipPath id="wgi-eyeClip">
          <path d="M28 130 Q130 34 232 130 Q130 226 28 130 Z" />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="260" height="260" rx="34" fill="#0A0D0B" />
      <rect x="1.5" y="1.5" width="257" height="257" rx="32.5" fill="none" stroke="#17301F" strokeWidth="3" />
      <circle cx="130" cy="130" r="115" fill="url(#wgi-glow)" />
      <g clipPath="url(#wgi-eyeClip)">
        <rect x="20" y="20" width="230" height="220" fill="url(#wgi-lid)" />
        <circle cx="130" cy="130" r="47" fill="url(#wgi-iris)" />
        <circle cx="130" cy="130" r="47" fill="none" stroke="#0A2E1C" strokeWidth="3" />
        <g stroke="#0B3A22" strokeOpacity="0.45" strokeWidth="2">
          <path d="M130 88 L130 172 M88 130 L172 130 M100 100 L160 160 M160 100 L100 160" />
        </g>
        <path d="M130 103 L153 144 L107 144 Z" fill="url(#wgi-pupil)" />
        <path d="M130 103 L153 144 L107 144 Z" fill="none" stroke="#6BFFA8" strokeWidth="2.5" strokeLinejoin="round" strokeOpacity="0.85" />
        <ellipse cx="108" cy="108" rx="13" ry="9" fill="#EAFFF2" opacity="0.5" transform="rotate(-25 108 108)" />
      </g>
      <path d="M28 130 Q130 34 232 130" fill="none" stroke="#3CE98C" strokeWidth="6" strokeLinecap="round" />
      <path d="M28 130 Q130 226 232 130" fill="none" stroke="#17A45D" strokeWidth="6" strokeLinecap="round" />
      <g stroke="#1B6B3F" strokeWidth="5" strokeLinecap="round">
        <path d="M60 74 L50 58" />
        <path d="M130 48 L130 30" />
        <path d="M200 74 L210 58" />
      </g>
    </svg>
  );
}
